import { useEffect, useState, useCallback, useRef } from "react";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Store, Package, DollarSign, TrendingUp, ShoppingBag, Loader2, Star, FileText, FileWarning, Download, Printer, Maximize2, Video, Trash2, Plus, Truck, Inbox as InboxIcon, MessageSquare, CheckCircle2, LayoutTemplate, BarChart3, Upload, RefreshCw, ImageIcon, Bell } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdminShell } from "@/components/admin/AdminShell";
import { AdminSupportInbox } from "@/components/admin/AdminSupportInbox";
import { useSupport } from "@/context/SupportContext";
import { TenantManagement } from "@/components/admin/TenantManagement";
import { FinanceModule } from "@/components/admin/FinanceModule";
import { SubscriptionManager } from "@/components/admin/SubscriptionManager";
import { CouponsManager } from "@/components/admin/CouponsManager";
import { ReviewsManager } from "@/components/admin/ReviewsManager";
import { ReturnsManager } from "@/components/admin/ReturnsManager";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { isSafeDoc, isImageDoc, isPdfDoc, downloadDoc, printDoc, buildCombinedPdf } from "@/lib/docUtils";
import { ShipmentManager } from "@/components/ShipmentManager";
import { ProductForm } from "@/components/seller/ProductForm";
import { AgeVerificationManager } from "@/components/AgeVerificationManager";
import { PartnerManager } from "@/components/admin/PartnerManager";
import { SellerVerificationManager } from "@/components/admin/SellerVerificationManager";
import { HeroPromoManager } from "@/components/admin/HeroPromoManager";

const DOC_KEYS = [
  ["gov_id_doc", "govId"],
  ["business_cert_doc", "businessCert"],
  ["tax_cert_doc", "taxCert"],
  ["export_license_doc", "exportLicence"],
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

function DocViewer({ url, label }) {
  const { t } = useI18n();
  if (!url) return (
    <div className="p-3 rounded-lg border border-dashed flex items-center gap-2 text-sm text-muted-foreground">
      <FileWarning className="w-4 h-4" /> {label}: <span className="italic">{t("notProvided")}</span>
    </div>
  );
  if (!isSafeDoc(url)) return (
    <div className="p-3 rounded-lg border border-dashed flex items-center gap-2 text-sm text-destructive">
      <FileWarning className="w-4 h-4" /> {label}: <span className="italic">Blocked (unsafe document link)</span>
    </div>
  );
  const image = isImageDoc(url);
  const fileName = `${label.replace(/[^a-z0-9]+/gi, "_")}.${isPdfDoc(url) ? "pdf" : "img"}`;
  return (
    <div className="rounded-lg border p-3" data-testid={`doc-${label.replace(/[^a-z0-9]+/gi, "-")}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <p className="text-xs font-semibold flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{label}</p>
        <div className="flex items-center gap-1.5">
          <Button size="icon" variant="ghost" className="h-7 w-7" title="Open full size" onClick={() => window.open(url, "_blank", "noopener")}><Maximize2 className="w-3.5 h-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" title="Print" data-testid={`print-doc-btn`} onClick={() => printDoc(url)}><Printer className="w-3.5 h-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" title="Download" data-testid={`download-doc-btn`} onClick={() => downloadDoc(url, fileName)}><Download className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      {image ? (
        <button type="button" onClick={() => window.open(url, "_blank", "noopener")} className="block w-full">
          <img src={url} alt={label} className="max-h-80 rounded object-contain w-full bg-muted cursor-zoom-in" />
        </button>
      ) : (
        <object data={url} type="application/pdf" className="w-full h-80 rounded bg-muted">
          <a href={url} target="_blank" rel="noreferrer noopener" className="text-sm text-primary underline">{t("openDocument")} ↗</a>
        </object>
      )}
    </div>
  );
}

function SellerDocsDialog({ seller }) {
  const { t } = useI18n();
  const flags = seller.doc_flags || {};
  const provided = DOC_KEYS.filter(([k]) => flags[k]).length;
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(false);
  const onOpenChange = async (v) => {
    setOpen(v);
    if (v && !docs) {
      setLoading(true);
      try {
        const r = await api.get(`/admin/sellers/${seller.user_id}/docs`);
        setDocs(r.data);
      } catch (e) { toast.error("Failed to load documents"); }
      finally { setLoading(false); }
    }
  };
  const downloadAll = async () => {
    setBusy(true);
    try {
      const src = docs || {};
      const items = DOC_KEYS.filter(([k]) => src[k]).map(([k, lk]) => ({ url: src[k], label: t(lk) }));
      if (items.length === 0) { toast.error("No documents to download"); return; }
      await buildCombinedPdf(items, seller.business_name || seller.name);
      toast.success("Combined PDF downloaded");
    } catch (e) {
      toast.error("Failed to build PDF");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid={`view-docs-${seller.user_id}`} size="sm" variant="outline">
          <FileText className="w-3.5 h-3.5 mr-1" />{t("verifyDocuments")} ({provided}/4)
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("verifyDocuments")} — {seller.business_name || seller.name}</DialogTitle></DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground grid grid-cols-2 gap-2">
            <span>{t("phoneLabel")}: {(docs || seller).phone || "—"}</span>
            <span>{t("thCountry")}: {(docs || seller).country || "—"}</span>
            <span>{t("taxNo")}: {(docs || seller).tax_number || "—"}</span>
            <span>{t("bank")}: {(docs || seller).bank_details || "—"}</span>
          </div>
          <Button data-testid={`download-all-docs-${seller.user_id}`} onClick={downloadAll} disabled={busy || provided === 0} className="w-full rounded-full" variant="secondary">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download all as PDF ({provided}/4)
          </Button>
          {DOC_KEYS.map(([k, lk]) => <DocViewer key={k} url={(docs || {})[k]} label={t(lk)} />)}
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


export default function AdminDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const support = useSupport();
  const [params, setParams] = useSearchParams();
  const section = params.get("section") || "overview";
  const setSection = (s) => setParams({ section: s });
  const [stats, setStats] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [videoAds, setVideoAds] = useState([]);
  const [vad, setVad] = useState({ title: "", embed_url: "" });
  const [imageAds, setImageAds] = useState([]);
  const [iad, setIad] = useState({ title: "", image_url: "", link_url: "", start_at: "", end_at: "" });
  const [prodCats, setProdCats] = useState([]);
  const [pfOpen, setPfOpen] = useState(false);
  const [pfEditing, setPfEditing] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [content, setContent] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [uploading, setUploading] = useState("");
  const [tenants, setTenants] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [auditFilter, setAuditFilter] = useState("");
  const [promoteEmail, setPromoteEmail] = useState("");
  const [custDetail, setCustDetail] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/sellers").then((r) => setSellers(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/withdrawals").then((r) => setWithdrawals(r.data)).catch(() => {});
    api.get("/admin/video-ads").then((r) => setVideoAds(r.data)).catch(() => {});
    api.get("/admin/image-ads").then((r) => setImageAds(r.data)).catch(() => {});
    api.get("/categories").then((r) => setProdCats(r.data)).catch(() => {});
    api.get("/admin/inbox").then((r) => setInbox(r.data)).catch(() => {});
    api.get("/admin/delivery-agents").then((r) => setDeliveryAgents(r.data)).catch(() => {});
    api.get("/content/home").then((r) => setContent(r.data)).catch(() => {});
    api.get("/admin/tenants").then((r) => setTenants(r.data)).catch(() => {});
    api.get("/admin/audit-logs?limit=200").then((r) => setAuditLogs(r.data)).catch(() => {});
    api.get("/admin/admins").then((r) => setAdmins(r.data.admins || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadAudit = useCallback((action) => {
    api.get(`/admin/audit-logs?limit=200${action ? `&action=${encodeURIComponent(action)}` : ""}`).then((r) => setAuditLogs(r.data)).catch(() => {});
  }, []);
  const setAdminRole = async (uid, admin_role) => {
    try { await api.post(`/admin/admins/${uid}/role`, { admin_role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const promoteByEmail = async () => {
    const u = users.find((x) => (x.email || "").toLowerCase() === promoteEmail.trim().toLowerCase());
    if (!u) return toast.error("No user found with that email");
    await setAdminRole(u.user_id, "admin");
    setPromoteEmail("");
  };

  // Real-time analytics + new-order push notifications: auto-refresh every 10s
  const prevOrdersRef = useRef(null);
  const [newOrders, setNewOrders] = useState(0);
  useEffect(() => {
    const fetchLive = () => api.get("/admin/analytics/live").then((r) => {
      setAnalytics(r.data);
      const total = r.data?.orders_total ?? 0;
      const prev = prevOrdersRef.current;
      if (prev !== null && total > prev) {
        const diff = total - prev;
        setNewOrders((n) => n + diff);
        toast.success(`${diff} new paid order${diff > 1 ? "s" : ""} received`, {
          description: "Open Orders to fulfil.",
          icon: <Bell className="w-4 h-4" />,
        });
      }
      prevOrdersRef.current = total;
    }).catch(() => {});
    fetchLive();
    const iv = setInterval(fetchLive, 10000);
    return () => clearInterval(iv);
  }, []);

  // Clear the new-order badge when the admin opens the Orders section.
  useEffect(() => { if (section === "orders") setNewOrders(0); }, [section]);

  const mediaUrl = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  const uploadFile = async (file, slot) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(slot);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); return null; }
    finally { setUploading(""); }
  };

  const saveContent = async (patch) => {
    try {
      const { data } = await api.put("/admin/content/home", { ...content, ...patch });
      setContent(data); toast.success("Homepage content saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };
  const setContentField = (k, v) => setContent((c) => ({ ...c, [k]: v }));
  const addBanner = () => setContent((c) => ({ ...c, banners: [...(c.banners || []), { title: "", subtitle: "", image: "", link: "/products" }] }));
  const updateBanner = (i, patch) => setContent((c) => ({ ...c, banners: (c.banners || []).map((b, idx) => (idx === i ? { ...b, ...patch } : b)) }));
  const removeBanner = (i) => setContent((c) => ({ ...c, banners: (c.banners || []).filter((_, idx) => idx !== i) }));

  const inboxStatus = async (kind, id, status) => {
    try { await api.post(`/admin/inbox/${kind}/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deliveryAction = async (id, action) => {
    try { await api.post(`/admin/delivery-agents/${id}/${action}`); toast.success(`Agent ${action}d`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const assignDelivery = async (orderId, agentId) => {
    try { await api.post(`/admin/orders/${orderId}/assign-delivery`, { agent_id: agentId }); toast.success("Delivery assigned"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to assign"); }
  };

  const addVideoAd = async () => {
    if (!vad.title.trim() || !vad.embed_url.trim()) return toast.error("Enter a title and video link");
    try {
      await api.post("/admin/video-ads", { title: vad.title, embed_url: vad.embed_url, active: true });
      toast.success("Video ad added"); setVad({ title: "", embed_url: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
  };
  const toggleVideoAd = async (id) => { await api.post(`/admin/video-ads/${id}/toggle`); load(); };
  const deleteVideoAd = async (id) => { await api.delete(`/admin/video-ads/${id}`); toast.success("Deleted"); load(); };

  const uploadImageAd = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const url = await uploadFile(file, "imagead");
    if (url) setIad((s) => ({ ...s, image_url: url }));
  };
  const addImageAd = async () => {
    if (!iad.image_url) return toast.error("Upload an advert image first");
    if (iad.start_at && iad.end_at && iad.end_at < iad.start_at) return toast.error("End date can't be before start date");
    try {
      await api.post("/admin/image-ads", { title: iad.title, image_url: iad.image_url, link_url: iad.link_url || null, start_at: iad.start_at || null, end_at: iad.end_at || null, active: true });
      toast.success("Image advert added"); setIad({ title: "", image_url: "", link_url: "", start_at: "", end_at: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
  };
  const toggleImageAd = async (id) => { await api.post(`/admin/image-ads/${id}/toggle`); load(); };
  const deleteImageAd = async (id) => { await api.delete(`/admin/image-ads/${id}`); toast.success("Deleted"); load(); };

  const sellerAction = async (id, action) => { await api.post(`/admin/sellers/${id}/${action}`); toast.success(`Seller ${action}d`); load(); };
  const removeStore = async (id) => {
    if (!window.confirm("Permanently remove this store and DELETE all its products from the front page? This cannot be undone.")) return;
    try {
      const { data } = await api.post(`/admin/sellers/${id}/remove-store`);
      toast.success(`Store removed — ${data.products_removed} product(s) deleted`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove store"); }
  };
  const productAction = async (id, action) => { await api.post(`/admin/products/${id}/${action}`); toast.success(`Product ${action}`); load(); };
  const userAction = async (id, action) => { await api.post(`/admin/users/${id}/${action}`); toast.success(`User ${action}d`); load(); };
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const doImpersonate = async (u) => {
    try {
      const target = await impersonate(u.user_id);
      toast.success(`Now viewing as ${target.name}`);
      navigate(target.role === "seller" ? "/seller" : "/");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to impersonate"); }
  };
  const refund = async (id) => { await api.post(`/admin/orders/${id}/refund`); toast.success("Refunded"); load(); };
  const approveWd = async (id) => { await api.post(`/admin/withdrawals/${id}/approve`); toast.success("Paid"); load(); };

  if (!stats) return <div className="min-h-screen flex items-center justify-center bg-muted/40"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const cards = [
    { icon: DollarSign, label: t("gmv"), value: `€${stats.gmv.toFixed(2)}`, color: "text-green-600" },
    { icon: TrendingUp, label: t("commissionPct"), value: `€${stats.commission.toFixed(2)}`, color: "text-primary" },
    { icon: ShoppingBag, label: t("ordersLabel"), value: stats.total_orders, color: "text-foreground" },
    { icon: Users, label: t("users"), value: stats.users, color: "text-muted-foreground" },
    { icon: Store, label: t("sellers"), value: stats.sellers, color: "text-foreground" },
    { icon: Package, label: t("productsLabel"), value: stats.products, color: "text-muted-foreground" },
  ];

  const badges = {
    pending_sellers: stats.pending_sellers || 0,
    pending_products: stats.pending_products || 0,
    pending_delivery: deliveryAgents.filter((a) => a.delivery_status === "pending").length,
    inbox_open: inbox.filter((i) => ["open", "received"].includes(i.status)).length,
    new_orders: newOrders,
    pending_returns: stats.pending_returns || 0,
    pending_age_verifications: stats.pending_age_verifications || 0,
    support_open: support?.staffUnread || 0,
  };

  const sectionMeta = {
    overview: { title: t("adminControlRoom"), subtitle: t("manageMarketplace") },
    products: { title: t("productsLabel"), subtitle: "Approve & manage catalogue" },
    orders: { title: t("ordersLabel"), subtitle: "All marketplace orders" },
    returns: { title: "Returns & Refunds", subtitle: "Review buyer returns and issue wallet refunds" },
    age: { title: "Age Verification", subtitle: "Approve buyer IDs for alcohol purchases" },
    partners: { title: "Affiliates & Influencers", subtitle: "Manage your referral partners, rates and payouts" },
    sellers: { title: t("sellers"), subtitle: "Vendor approvals & management" },
    verifications: { title: "Seller Verification", subtitle: "Review ID, selfie & bank docs — manually or with AI" },
    "hero-promos": { title: "Hero Promos", subtitle: "Sponsored products shown in the homepage hero showcase" },
    delivery: { title: "Delivery", subtitle: "Delivery agents & assignments" },
    tenants: { title: "Tenant Management", subtitle: "View, approve & manage every store" },
    audit: { title: "Audit Logs", subtitle: "Every platform change, tracked" },
    roles: { title: "Roles & Access (RBAC)", subtitle: "Manage admin accounts & permissions" },
    users: { title: "Customers", subtitle: "All registered users" },
    reviews: { title: "Reviews", subtitle: "Moderate product reviews" },
    coupons: { title: "Coupons", subtitle: "Platform & seller discount codes" },
    inbox: { title: "Inbox", subtitle: "Messages, disputes & complaints" },
    support: { title: "Customer Support", subtitle: "Live customer-care conversations" },
    finance: { title: "Payments & Payouts", subtitle: "Withdrawal requests" },
    revenue: { title: "Finance & Revenue", subtitle: "Commissions, transactions & revenue trends" },
    subscriptions: { title: "Subscriptions", subtitle: "Seller plans, trials & commission" },
    analytics: { title: "Live Analytics", subtitle: "Real-time performance" },
    content: { title: "Site Content", subtitle: "Homepage hero & banners" },
    videoads: { title: "Video Ads", subtitle: "Homepage video adverts" },
    imageads: { title: "Image Ads", subtitle: "Homepage image advert slider" },
    settings: { title: "Settings", subtitle: "Console preferences" },
  }[section] || {};

  return (
    <AdminShell active={section} onNavigate={setSection} badges={badges} title={sectionMeta.title} subtitle={sectionMeta.subtitle}>
      <Tabs value={section} onValueChange={setSection}>
        <TabsContent value="overview" className="mt-0">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            {cards.map((c) => (
              <Card key={c.label} className="p-4 shadow-sm"><c.icon className={`w-5 h-5 ${c.color} mb-2`} /><p className="text-xl font-black font-display">{c.value}</p><p className="text-xs text-muted-foreground">{c.label}</p></Card>
            ))}
          </div>
          <Card className="p-5 shadow-sm"><h3 className="font-semibold mb-4">{t("marketplaceSales")}</h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={stats.sales_series}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} /><stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Area type="monotone" dataKey="amount" stroke="hsl(var(--primary))" fill="url(#g)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

          <TabsContent value="revenue" className="mt-0"><FinanceModule /></TabsContent>
          <TabsContent value="subscriptions" className="mt-0"><SubscriptionManager /></TabsContent>
          <TabsContent value="coupons" className="mt-0"><CouponsManager /></TabsContent>
          <TabsContent value="reviews" className="mt-0"><ReviewsManager /></TabsContent>

          <TabsContent value="support" className="mt-0"><AdminSupportInbox /></TabsContent>
          <TabsContent value="returns" className="mt-0"><ReturnsManager onResolved={load} /></TabsContent>
          <TabsContent value="age" className="mt-0"><AgeVerificationManager onChanged={load} /></TabsContent>
          <TabsContent value="partners" className="mt-0"><PartnerManager /></TabsContent>
          <TabsContent value="verifications" className="mt-0"><SellerVerificationManager /></TabsContent>
          <TabsContent value="hero-promos" className="mt-0"><HeroPromoManager /></TabsContent>

          <TabsContent value="sellers" className="mt-6">
            <Card><Table><TableHeader><TableRow><TableHead>{t("thBusiness")}</TableHead><TableHead>{t("thCountry")}</TableHead><TableHead>{t("thStatus")}</TableHead><TableHead>{t("thActions")}</TableHead></TableRow></TableHeader>
              <TableBody>{sellers.map((s) => (
                <TableRow key={s.user_id} data-testid={`seller-row-${s.user_id}`}>
                  <TableCell><p className="font-medium">{s.business_name || s.name}</p><p className="text-xs text-muted-foreground">{s.email}</p></TableCell>
                  <TableCell>{s.country || "—"}</TableCell>
                  <TableCell><Badge className={s.seller_status === "approved" ? "bg-green-600" : s.seller_status === "pending" ? "bg-amber-500" : "bg-red-500"}>{t("status" + (s.seller_status || "pending").charAt(0).toUpperCase() + (s.seller_status || "pending").slice(1))}</Badge></TableCell>
                  <TableCell className="flex gap-1 flex-wrap">
                    <SellerDocsDialog seller={s} />
                    <Button data-testid={`approve-seller-${s.user_id}`} size="sm" variant="outline" onClick={() => sellerAction(s.user_id, "approve")} disabled={s.seller_status === "approved"}>{t("approve")}</Button>
                    <Button data-testid={`reject-seller-${s.user_id}`} size="sm" variant="outline" onClick={() => sellerAction(s.user_id, "reject")}>{t("reject")}</Button>
                    <Button data-testid={`suspend-seller-${s.user_id}`} size="sm" variant="outline" onClick={() => sellerAction(s.user_id, "suspend")}>{t("suspend")}</Button>
                    <Button data-testid={`remove-store-${s.user_id}`} size="sm" variant="destructive" onClick={() => removeStore(s.user_id)}>Remove store</Button>
                  </TableCell>
                </TableRow>
              ))}</TableBody></Table></Card>
          </TabsContent>

          <TabsContent value="products" className="mt-6">
            <Card><Table><TableHeader><TableRow><TableHead>{t("thProduct")}</TableHead><TableHead>Seller / Store</TableHead><TableHead>{t("thPrice")}</TableHead><TableHead>Listed</TableHead><TableHead>Last sold</TableHead><TableHead>{t("thStatus")}</TableHead><TableHead>{t("thActions")}</TableHead></TableRow></TableHeader>
              <TableBody>{products.map((p) => (
                <TableRow key={p.product_id} data-testid={`admin-prod-row-${p.product_id}`}>
                  <TableCell className="flex items-center gap-2"><img src={p.images?.[0]} alt="" className="w-8 h-8 rounded object-cover" /><span className="text-sm">{p.title}</span>{p.featured && <Star className="w-3 h-3 fill-gold text-gold" />}</TableCell>
                  <TableCell data-testid={`prod-seller-${p.product_id}`}><span className="text-sm font-medium">{p.seller_name}</span></TableCell>
                  <TableCell>€{p.price}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap" data-testid={`prod-listed-${p.product_id}`}>{fmtDate(p.created_at)}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap" data-testid={`prod-lastsold-${p.product_id}`}>{fmtDate(p.last_sold_at)}</TableCell>
                  <TableCell><Badge className={p.status === "approved" ? "bg-green-600" : p.status === "pending" ? "bg-amber-500" : "bg-red-500"}>{t("status" + p.status.charAt(0).toUpperCase() + p.status.slice(1))}</Badge></TableCell>
                  <TableCell className="flex gap-1 flex-wrap">
                    <Button data-testid={`edit-prod-${p.product_id}`} size="sm" variant="outline" onClick={() => { setPfEditing(p); setPfOpen(true); }}>Edit</Button>
                    <Button data-testid={`approve-prod-${p.product_id}`} size="sm" variant="outline" onClick={() => productAction(p.product_id, "approve")} disabled={p.status === "approved"}>{t("approve")}</Button>
                    <Button size="sm" variant="outline" onClick={() => productAction(p.product_id, "feature")}>{p.featured ? t("unfeature") : t("feature")}</Button>
                    <Button data-testid={`remove-prod-${p.product_id}`} size="sm" variant="outline" onClick={() => productAction(p.product_id, "remove")}>{t("remove")}</Button>
                  </TableCell>
                </TableRow>
              ))}</TableBody></Table></Card>
            <ProductForm open={pfOpen} onOpenChange={setPfOpen} product={pfEditing} cats={prodCats} sellCurrency="EUR" onSaved={() => { setPfOpen(false); setPfEditing(null); toast.success("Product updated"); load(); }} />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card><Table><TableHeader><TableRow><TableHead>{t("thName")}</TableHead><TableHead>{t("thEmail")}</TableHead><TableHead>{t("thRole")}</TableHead><TableHead>{t("thStatus")}</TableHead><TableHead>{t("thActions")}</TableHead></TableRow></TableHeader>
              <TableBody>{users.map((u) => (
                <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <TableCell><button className="hover:underline text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded" onClick={() => setCustDetail(u)} data-testid={`user-detail-${u.user_id}`}>{u.name}</button></TableCell><TableCell className="text-xs">{u.email}</TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{u.role}</Badge></TableCell>
                  <TableCell><Badge className={u.status === "active" ? "bg-green-600" : "bg-red-500"}>{u.status}</Badge></TableCell>
                  <TableCell className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => userAction(u.user_id, u.status === "active" ? "suspend" : "activate")}>{u.status === "active" ? t("suspend") : t("activate")}</Button>
                    {u.role !== "admin" && <Button data-testid={`impersonate-${u.user_id}`} size="sm" variant="secondary" onClick={() => doImpersonate(u)}>Impersonate</Button>}
                  </TableCell>
                </TableRow>
              ))}</TableBody></Table></Card>
            <Dialog open={!!custDetail} onOpenChange={(o) => !o && setCustDetail(null)}>
              <DialogContent className="max-w-md" data-testid="customer-detail-dialog">
                {custDetail && (() => {
                  const custOrders = orders.filter((o) => o.user_id === custDetail.user_id);
                  const paidOrders = custOrders.filter((o) => o.payment_status === "paid");
                  const spent = paidOrders.reduce((s, o) => s + (o.total || 0), 0);
                  return (
                    <>
                      <DialogHeader><DialogTitle>{custDetail.name}</DialogTitle></DialogHeader>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center gap-3">
                          <span className="w-12 h-12 rounded-full bg-primary/15 text-primary font-bold flex items-center justify-center text-lg">{(custDetail.name || "U").charAt(0).toUpperCase()}</span>
                          <div><p className="font-medium">{custDetail.email}</p><Badge variant="outline" className="capitalize mt-1">{custDetail.role}</Badge></div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 pt-2">
                          <div className="rounded-xl border p-3 text-center"><p className="text-lg font-black font-display">{paidOrders.length}</p><p className="text-[11px] text-muted-foreground">Orders</p></div>
                          <div className="rounded-xl border p-3 text-center"><p className="text-lg font-black font-display">€{spent.toFixed(2)}</p><p className="text-[11px] text-muted-foreground">Spent</p></div>
                          <div className="rounded-xl border p-3 text-center"><p className="text-lg font-black font-display">{custDetail.created_at ? new Date(custDetail.created_at).toLocaleDateString(undefined, { month: "short", year: "2-digit" }) : "—"}</p><p className="text-[11px] text-muted-foreground">Joined</p></div>
                        </div>
                        <div><p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Status</p><Badge className={custDetail.status === "active" ? "bg-green-600" : "bg-red-500"}>{custDetail.status}</Badge></div>
                      </div>
                    </>
                  );
                })()}
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <Card><Table><TableHeader><TableRow><TableHead>{t("thOrder")}</TableHead><TableHead>{t("thTotal")}</TableHead><TableHead>{t("thPayment")}</TableHead><TableHead>{t("thStatus")}</TableHead><TableHead>{t("thActions")}</TableHead></TableRow></TableHeader>
              <TableBody>{orders.map((o) => (
                <TableRow key={o.order_id}>
                  <TableCell className="font-mono text-xs">#{o.order_id.slice(-8)}</TableCell>
                  <TableCell>€{o.total.toFixed(2)}</TableCell>
                  <TableCell><Badge className={o.payment_status === "paid" ? "bg-green-600" : "bg-amber-500"}>{o.payment_status}</Badge></TableCell>
                  <TableCell className="capitalize">{o.status.replace("_", " ")}</TableCell>
                  <TableCell className="flex gap-1 flex-wrap items-center">
                    <Dialog>
                      <DialogTrigger asChild><Button data-testid={`track-order-${o.order_id}`} size="sm" variant="outline"><Truck className="w-3.5 h-3.5 mr-1" />Track</Button></DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader><DialogTitle>Shipment — #{o.order_id.slice(-8)}</DialogTitle></DialogHeader>
                        <ShipmentManager order={o} onUpdated={() => load()} />
                      </DialogContent>
                    </Dialog>
                    <Select value={o.delivery_agent_id || ""} onValueChange={(v) => assignDelivery(o.order_id, v)}>
                      <SelectTrigger data-testid={`assign-delivery-${o.order_id}`} className="h-8 w-40 text-xs"><SelectValue placeholder={o.delivery_agent_name || "Assign delivery"} /></SelectTrigger>
                      <SelectContent>
                        {deliveryAgents.filter((a) => a.delivery_status === "approved").map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name} · {a.delivery_city || "—"}</SelectItem>)}
                        {deliveryAgents.filter((a) => a.delivery_status === "approved").length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No approved agents</div>}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => refund(o.order_id)} disabled={o.status === "refunded"}>{t("refund")}</Button>
                  </TableCell>
                </TableRow>
              ))}</TableBody></Table></Card>
          </TabsContent>

          <TabsContent value="finance" className="mt-6">
            <Card><Table><TableHeader><TableRow><TableHead>{t("thSeller")}</TableHead><TableHead>{t("thAmount")}</TableHead><TableHead>{t("thStatus")}</TableHead><TableHead>{t("thAction")}</TableHead></TableRow></TableHeader>
              <TableBody>{withdrawals.map((w) => (
                <TableRow key={w.withdrawal_id}>
                  <TableCell className="font-mono text-xs">{w.seller_id.slice(-8)}</TableCell>
                  <TableCell>€{w.amount.toFixed(2)}</TableCell>
                  <TableCell><Badge className={w.status === "paid" ? "bg-green-600" : "bg-amber-500"}>{w.status}</Badge></TableCell>
                  <TableCell><Button size="sm" variant="outline" onClick={() => approveWd(w.withdrawal_id)} disabled={w.status === "paid"}>{t("markPaid")}</Button></TableCell>
                </TableRow>
              ))}
              {withdrawals.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">{t("noWithdrawals")}</TableCell></TableRow>}</TableBody></Table></Card>
          </TabsContent>

          <TabsContent value="delivery" className="mt-6">
            <Card><Table><TableHeader><TableRow><TableHead>Agent</TableHead><TableHead>City / Vehicle</TableHead><TableHead>Assigned</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>{deliveryAgents.map((a) => (
                <TableRow key={a.user_id} data-testid={`delivery-agent-row-${a.user_id}`}>
                  <TableCell><p className="font-medium">{a.name}</p><p className="text-xs text-muted-foreground">{a.email}</p></TableCell>
                  <TableCell className="text-sm">{a.delivery_city || "—"}{a.delivery_vehicle ? ` · ${a.delivery_vehicle}` : ""}</TableCell>
                  <TableCell>{a.assigned_orders}</TableCell>
                  <TableCell><Badge className={a.delivery_status === "approved" ? "bg-green-600" : a.delivery_status === "pending" ? "bg-amber-500" : "bg-muted-foreground"}>{a.delivery_status}</Badge></TableCell>
                  <TableCell className="flex gap-1 flex-wrap">
                    {a.delivery_status !== "approved" && <Button data-testid={`approve-agent-${a.user_id}`} size="sm" variant="outline" onClick={() => deliveryAction(a.user_id, "approve")}>Approve</Button>}
                    {a.delivery_status === "pending" && <Button size="sm" variant="outline" onClick={() => deliveryAction(a.user_id, "reject")}>Reject</Button>}
                    {a.delivery_status === "approved" && <Button data-testid={`suspend-agent-${a.user_id}`} size="sm" variant="outline" onClick={() => deliveryAction(a.user_id, "suspend")}>Suspend</Button>}
                  </TableCell>
                </TableRow>
              ))}
              {deliveryAgents.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No delivery agents yet. Agents can sign up with the "Delivery" role.</TableCell></TableRow>}</TableBody></Table></Card>
          </TabsContent>

          <TabsContent value="inbox" className="mt-6">
            <Card><Table><TableHeader><TableRow><TableHead>Type</TableHead><TableHead>From</TableHead><TableHead>Subject</TableHead><TableHead>Received</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>{inbox.map((i) => (
                <TableRow key={`${i.kind}-${i.id}`} data-testid={`inbox-row-${i.id}`}>
                  <TableCell><Badge variant="outline" className="capitalize">{i.kind}</Badge></TableCell>
                  <TableCell><p className="font-medium text-sm">{i.name}</p><p className="text-xs text-muted-foreground">{i.email}</p></TableCell>
                  <TableCell className="max-w-xs">
                    <Dialog>
                      <DialogTrigger asChild><button data-testid={`inbox-view-${i.id}`} className="text-left text-sm hover:text-primary truncate block max-w-[220px]">{i.subject}</button></DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquare className="w-4 h-4" /> {i.subject}</DialogTitle></DialogHeader>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-muted-foreground">From:</span> {i.name} ({i.email})</p>
                          {i.detail?.order_id && <p><span className="text-muted-foreground">Order:</span> {i.detail.order_id}</p>}
                          {i.detail?.infringing_url && <p className="break-all"><span className="text-muted-foreground">URL:</span> {i.detail.infringing_url}</p>}
                          {i.detail?.postal_address && <p><span className="text-muted-foreground">Address:</span> {i.detail.postal_address}</p>}
                          <div className="p-3 rounded-lg bg-muted/40 whitespace-pre-wrap">{i.detail?.description || i.detail?.details || i.detail?.message || "—"}</div>
                          <a href={`mailto:${i.email}`} className="inline-block text-primary text-sm hover:underline">Reply by email ↗</a>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </TableCell>
                  <TableCell className="text-xs">{fmtDate(i.created_at)}</TableCell>
                  <TableCell><Badge className={["open", "received"].includes(i.status) ? "bg-amber-500" : "bg-green-600"}>{i.status}</Badge></TableCell>
                  <TableCell>
                    {["open", "received"].includes(i.status)
                      ? <Button data-testid={`inbox-resolve-${i.id}`} size="sm" variant="outline" onClick={() => inboxStatus(i.kind, i.id, "resolved")}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Resolve</Button>
                      : <Button size="sm" variant="ghost" onClick={() => inboxStatus(i.kind, i.id, "open")}>Reopen</Button>}
                  </TableCell>
                </TableRow>
              ))}
              {inbox.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Inbox is empty — no messages, disputes or complaints yet.</TableCell></TableRow>}</TableBody></Table></Card>
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            {!analytics ? <div className="flex items-center gap-2 text-muted-foreground py-8"><Loader2 className="w-5 h-5 animate-spin" />Loading live data…</div> : (
              <div className="space-y-5">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />Live</span>
                  <RefreshCw className="w-3.5 h-3.5" />Auto-refreshing every 10s · updated {fmtDate(analytics.server_time)}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {[
                    { label: "GMV (all)", value: `€${analytics.gmv.toFixed(2)}`, color: "text-green-600" },
                    { label: "GMV today", value: `€${analytics.gmv_today.toFixed(2)}`, color: "text-primary" },
                    { label: "Orders today", value: analytics.orders_today, color: "text-secondary" },
                    { label: "Orders (all)", value: analytics.orders_total, color: "text-blue-600" },
                    { label: "Active carts", value: analytics.active_carts, color: "text-gold" },
                    { label: "Pending", value: analytics.pending_orders, color: "text-amber-600" },
                  ].map((c) => (
                    <Card key={c.label} className="p-4" data-testid={`live-stat-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                      <p className={`text-xl font-black font-display ${c.color}`}>{c.value}</p>
                      <p className="text-xs text-muted-foreground">{c.label}</p>
                    </Card>
                  ))}
                </div>
                <Card className="p-5"><h3 className="font-semibold mb-4">Orders — last 24 hours</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={analytics.orders_24h}>
                      <defs><linearGradient id="ga" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} /><stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient></defs>
                      <XAxis dataKey="hour" fontSize={10} interval={2} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Area type="monotone" dataKey="orders" stroke="hsl(var(--primary))" fill="url(#ga)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>
                <Card className="p-5"><h3 className="font-semibold mb-3">Top products by units sold</h3>
                  <div className="space-y-2">
                    {analytics.top_products.map((p, i) => (
                      <div key={p.product_id} className="flex items-center gap-3" data-testid={`top-product-${i}`}>
                        <span className="text-sm font-bold w-5 text-muted-foreground">#{i + 1}</span>
                        <img src={p.images?.[0]} alt="" className="w-9 h-9 rounded object-cover bg-muted" />
                        <span className="flex-1 text-sm truncate">{p.title}</span>
                        <span className="text-sm font-semibold">{p.sold || 0} sold</span>
                      </div>
                    ))}
                    {analytics.top_products.length === 0 && <p className="text-sm text-muted-foreground">No sales data yet.</p>}
                  </div>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="content" className="mt-6">
            {!content ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <div className="space-y-5 max-w-3xl">
                <Card className="p-5 space-y-4">
                  <h3 className="font-semibold flex items-center gap-2"><LayoutTemplate className="w-4 h-4" />Homepage hero</h3>
                  <div><label className="text-sm font-medium">Headline</label><Input data-testid="cms-hero-title" value={content.hero_title || ""} onChange={(e) => setContentField("hero_title", e.target.value)} className="mt-1" /></div>
                  <div><label className="text-sm font-medium">Subtext</label><Textarea data-testid="cms-hero-subtitle" value={content.hero_subtitle || ""} onChange={(e) => setContentField("hero_subtitle", e.target.value)} className="mt-1" /></div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><label className="text-sm font-medium">Button text</label><Input data-testid="cms-cta-text" value={content.hero_cta_text || ""} onChange={(e) => setContentField("hero_cta_text", e.target.value)} className="mt-1" /></div>
                    <div><label className="text-sm font-medium">Button link</label><Input data-testid="cms-cta-link" value={content.hero_cta_link || ""} onChange={(e) => setContentField("hero_cta_link", e.target.value)} className="mt-1" placeholder="/products" /></div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium block mb-1">Hero image</label>
                      {content.hero_image && <img src={mediaUrl(content.hero_image)} alt="hero" className="w-full h-28 object-cover rounded-lg mb-2" />}
                      <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border cursor-pointer hover:bg-accent">
                        {uploading === "hero_image" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload image
                        <input data-testid="cms-hero-image-upload" type="file" accept="image/*" hidden onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const url = await uploadFile(f, "hero_image"); if (url) setContentField("hero_image", url); } }} />
                      </label>
                      {content.hero_image && <Button size="sm" variant="ghost" className="ml-2 text-destructive" onClick={() => setContentField("hero_image", "")}>Remove</Button>}
                    </div>
                    <div>
                      <label className="text-sm font-medium block mb-1">Hero video (optional, overrides image)</label>
                      {content.hero_video && <video src={mediaUrl(content.hero_video)} className="w-full h-28 object-cover rounded-lg mb-2" muted />}
                      <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border cursor-pointer hover:bg-accent">
                        {uploading === "hero_video" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload video
                        <input data-testid="cms-hero-video-upload" type="file" accept="video/*" hidden onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const url = await uploadFile(f, "hero_video"); if (url) setContentField("hero_video", url); } }} />
                      </label>
                      {content.hero_video && <Button size="sm" variant="ghost" className="ml-2 text-destructive" onClick={() => setContentField("hero_video", "")}>Remove</Button>}
                    </div>
                  </div>
                </Card>

                <Card className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold flex items-center gap-2"><ImageIcon className="w-4 h-4" />Promo banners</h3>
                    <Button size="sm" variant="outline" data-testid="cms-add-banner" onClick={addBanner}><Plus className="w-4 h-4 mr-1" />Add banner</Button>
                  </div>
                  {(content.banners || []).map((b, i) => (
                    <div key={i} className="grid sm:grid-cols-2 gap-2 p-3 rounded-lg border" data-testid={`cms-banner-${i}`}>
                      <Input placeholder="Title" value={b.title || ""} onChange={(e) => updateBanner(i, { title: e.target.value })} />
                      <Input placeholder="Subtitle" value={b.subtitle || ""} onChange={(e) => updateBanner(i, { subtitle: e.target.value })} />
                      <Input placeholder="Link (/products?category=grocery)" value={b.link || ""} onChange={(e) => updateBanner(i, { link: e.target.value })} />
                      <div className="flex items-center gap-2">
                        <label className="inline-flex items-center gap-1.5 text-sm px-2.5 py-2 rounded-lg border cursor-pointer hover:bg-accent">
                          {uploading === `banner-${i}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Image
                          <input type="file" accept="image/*" hidden onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const url = await uploadFile(f, `banner-${i}`); if (url) updateBanner(i, { image: url }); } }} />
                        </label>
                        {b.image && <img src={mediaUrl(b.image)} alt="" className="w-10 h-10 rounded object-cover" />}
                        <button className="ml-auto" onClick={() => removeBanner(i)}><Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" /></button>
                      </div>
                    </div>
                  ))}
                  {(content.banners || []).length === 0 && <p className="text-sm text-muted-foreground">No banners. Add one to feature on the homepage.</p>}
                </Card>

                <Button data-testid="cms-save-btn" className="rounded-full" onClick={() => saveContent({})}>Save homepage content</Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="videoads" className="mt-6">
            <Card className="p-5 mb-4">
              <h3 className="font-semibold mb-1">Add a video advert</h3>
              <p className="text-xs text-muted-foreground mb-4">Paste a YouTube, Vimeo, or direct video (.mp4) link — or any platform embed URL. Active videos rotate in a banner on the homepage.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input data-testid="videoad-title" placeholder="Title (e.g. Summer Sale)" value={vad.title} onChange={(e) => setVad({ ...vad, title: e.target.value })} className="sm:max-w-xs" />
                <Input data-testid="videoad-url" placeholder="https://youtube.com/watch?v=..." value={vad.embed_url} onChange={(e) => setVad({ ...vad, embed_url: e.target.value })} />
                <Button data-testid="videoad-add-btn" onClick={addVideoAd} className="rounded-full"><Plus className="w-4 h-4 mr-1" />Add</Button>
              </div>
            </Card>
            <Card><Table><TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Link</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>{videoAds.map((v) => (
                <TableRow key={v.video_ad_id} data-testid={`videoad-row-${v.video_ad_id}`}>
                  <TableCell className="font-medium">{v.title}</TableCell>
                  <TableCell className="max-w-xs truncate"><a href={v.embed_url} target="_blank" rel="noreferrer noopener" className="text-primary underline text-xs">{v.embed_url}</a></TableCell>
                  <TableCell><Badge className={v.active ? "bg-green-600" : "bg-muted-foreground"}>{v.active ? "Live" : "Hidden"}</Badge></TableCell>
                  <TableCell className="flex gap-1 flex-wrap">
                    <Button data-testid={`videoad-toggle-${v.video_ad_id}`} size="sm" variant="outline" onClick={() => toggleVideoAd(v.video_ad_id)}>{v.active ? "Hide" : "Show"}</Button>
                    <Button data-testid={`videoad-delete-${v.video_ad_id}`} size="sm" variant="outline" onClick={() => deleteVideoAd(v.video_ad_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {videoAds.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No video adverts yet. Add one above.</TableCell></TableRow>}</TableBody></Table></Card>
          </TabsContent>

          <TabsContent value="imageads" className="mt-6">
            <Card className="p-5 mb-4">
              <h3 className="font-semibold mb-1">Add an image advert</h3>
              <p className="text-xs text-muted-foreground mb-4">Upload a wide banner image (16:6 works best). Active adverts appear in a sliding banner on the homepage with left/right arrows.</p>
              <div className="grid sm:grid-cols-2 gap-3 items-start">
                <div className="space-y-2">
                  <Input data-testid="imagead-title" placeholder="Title (optional overlay text)" value={iad.title} onChange={(e) => setIad({ ...iad, title: e.target.value })} />
                  <Input data-testid="imagead-link" placeholder="Link when clicked (optional, e.g. /products)" value={iad.link_url} onChange={(e) => setIad({ ...iad, link_url: e.target.value })} />
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-[11px] text-muted-foreground">Start date (optional)</label><Input data-testid="imagead-start" type="date" value={iad.start_at} onChange={(e) => setIad({ ...iad, start_at: e.target.value })} /></div>
                    <div><label className="text-[11px] text-muted-foreground">End date (optional)</label><Input data-testid="imagead-end" type="date" value={iad.end_at} onChange={(e) => setIad({ ...iad, end_at: e.target.value })} /></div>
                  </div>
                  <label className="flex items-center gap-2 px-4 py-2 border border-dashed rounded-lg cursor-pointer hover:border-primary text-sm w-fit">
                    <Plus className="w-4 h-4" />{uploading === "imagead" ? "Uploading…" : "Upload image"}
                    <input type="file" data-testid="imagead-upload" accept="image/*" onChange={uploadImageAd} className="hidden" />
                  </label>
                  <Button data-testid="imagead-add-btn" onClick={addImageAd} disabled={!iad.image_url} className="rounded-full"><Plus className="w-4 h-4 mr-1" />Add advert</Button>
                </div>
                <div className="rounded-xl overflow-hidden border bg-muted aspect-[16/6] flex items-center justify-center">
                  {iad.image_url ? <img src={mediaUrl(iad.image_url)} alt="preview" className="w-full h-full object-cover" data-testid="imagead-preview" /> : <span className="text-xs text-muted-foreground">Image preview</span>}
                </div>
              </div>
            </Card>
            <Card><Table><TableHeader><TableRow><TableHead>Preview</TableHead><TableHead>Title</TableHead><TableHead>Link</TableHead><TableHead>Schedule</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>{imageAds.map((v) => (
                <TableRow key={v.image_ad_id} data-testid={`imagead-row-${v.image_ad_id}`}>
                  <TableCell><div className="w-24 h-10 rounded overflow-hidden bg-muted"><img src={mediaUrl(v.image_url)} alt="" className="w-full h-full object-cover" /></div></TableCell>
                  <TableCell className="font-medium">{v.title || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="max-w-[160px] truncate text-xs">{v.link_url || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{v.start_at || v.end_at ? `${v.start_at || "—"} → ${v.end_at || "—"}` : <span className="text-muted-foreground">Always</span>}</TableCell>
                  <TableCell>{(() => {
                    const today = new Date().toISOString().slice(0, 10);
                    let label = "Live", cls = "bg-green-600";
                    if (!v.active) { label = "Hidden"; cls = "bg-muted-foreground"; }
                    else if (v.start_at && today < v.start_at) { label = "Scheduled"; cls = "bg-blue-500"; }
                    else if (v.end_at && today > v.end_at) { label = "Expired"; cls = "bg-red-500"; }
                    return <Badge className={cls}>{label}</Badge>;
                  })()}</TableCell>
                  <TableCell className="flex gap-1 flex-wrap">
                    <Button data-testid={`imagead-toggle-${v.image_ad_id}`} size="sm" variant="outline" onClick={() => toggleImageAd(v.image_ad_id)}>{v.active ? "Hide" : "Show"}</Button>
                    <Button data-testid={`imagead-delete-${v.image_ad_id}`} size="sm" variant="outline" onClick={() => deleteImageAd(v.image_ad_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {imageAds.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No image adverts yet. Add one above.</TableCell></TableRow>}</TableBody></Table></Card>
          </TabsContent>

          <TabsContent value="tenants" className="mt-0">
            <TenantManagement tenants={tenants} onChanged={load} />
          </TabsContent>

          <TabsContent value="audit" className="mt-0">
            <div className="flex flex-wrap gap-1 mb-3">
              {[["", "All"], ["seller", "Sellers"], ["tenant", "Tenants"], ["product", "Products"], ["rbac", "Roles"], ["refund", "Refunds"]].map(([val, label]) => (
                <Button key={label} size="sm" variant={auditFilter === val ? "default" : "outline"} className="rounded-full h-8" data-testid={`audit-filter-${label.toLowerCase()}`} onClick={() => { setAuditFilter(val); loadAudit(val); }}>{label}</Button>
              ))}
            </div>
            <Card className="p-0 overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead>Target</TableHead></TableRow></TableHeader>
                <TableBody>
                  {auditLogs.map((l) => (
                    <TableRow key={l.log_id} data-testid={`audit-row-${l.log_id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                      <TableCell className="text-sm"><p className="font-medium">{l.actor_name || "—"}</p><p className="text-xs text-muted-foreground">{l.actor_email}</p></TableCell>
                      <TableCell><Badge variant="outline" className="font-mono text-[11px]">{l.action}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{l.target_type}{l.target_id ? ` · ${String(l.target_id).slice(-10)}` : ""}{l.meta?.new_status ? ` → ${l.meta.new_status}` : ""}</TableCell>
                    </TableRow>
                  ))}
                  {auditLogs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">No audit entries yet. Admin actions will appear here.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="roles" className="mt-0">
            {user?.admin_role !== "super_admin" ? (
              <Card className="p-8 text-center"><p className="text-muted-foreground">Only a <strong>super admin</strong> can manage roles & access.</p></Card>
            ) : (
              <div className="space-y-4 max-w-3xl">
                <Card className="p-5">
                  <h3 className="font-semibold mb-3">Grant admin access</h3>
                  <div className="flex gap-2">
                    <Input data-testid="promote-email" placeholder="Customer email to promote to admin" value={promoteEmail} onChange={(e) => setPromoteEmail(e.target.value)} />
                    <Button data-testid="promote-btn" className="rounded-full" onClick={promoteByEmail}>Make admin</Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">The user must sign in again after their role changes.</p>
                </Card>
                <Card className="p-0 overflow-hidden">
                  <Table>
                    <TableHeader><TableRow><TableHead>Admin</TableHead><TableHead>Role</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {admins.map((a) => (
                        <TableRow key={a.user_id} data-testid={`admin-row-${a.user_id}`}>
                          <TableCell><p className="font-medium text-sm">{a.name}</p><p className="text-xs text-muted-foreground">{a.email}</p></TableCell>
                          <TableCell><Badge className={a.admin_role === "super_admin" ? "bg-primary" : "bg-secondary text-secondary-foreground"}>{a.admin_role || "admin"}</Badge></TableCell>
                          <TableCell className="text-right space-x-1">
                            {a.admin_role !== "super_admin" && <Button size="sm" variant="outline" className="h-8" data-testid={`make-super-${a.user_id}`} onClick={() => setAdminRole(a.user_id, "super_admin")}>Make super</Button>}
                            {a.admin_role === "super_admin" && a.user_id !== user.user_id && <Button size="sm" variant="outline" className="h-8" onClick={() => setAdminRole(a.user_id, "admin")}>Demote</Button>}
                            {a.user_id !== user.user_id && <Button size="sm" variant="ghost" className="h-8 text-destructive" data-testid={`revoke-${a.user_id}`} onClick={() => setAdminRole(a.user_id, "none")}>Revoke</Button>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings" className="mt-0">
            <Card className="p-8 text-center shadow-sm">
              <p className="font-semibold text-lg mb-1">Console settings</p>
              <p className="text-muted-foreground text-sm">Use the currency and language switchers in the top bar. More administration preferences are coming soon.</p>
            </Card>
          </TabsContent>
        </Tabs>
    </AdminShell>
  );
}
